import { CSSProperties, ReactNode, Ref } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowClockwiseIcon,
  BookOpenTextIcon,
  ListNumbersIcon,
  MonitorPlayIcon,
  PresentationChartIcon,
  SparkleIcon
} from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@libs/zapi-ui/ds/components/empty";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { ToggleGroup, ToggleGroupItem } from "@libs/zapi-ui/ds/components/toggle-group";
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardSeparator
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/WidgetCard";
import {
  SECTION_STICKY_GAP,
  sectionStickyTop
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/SectionPage";
import { TViewMode } from "../model/types";

export type TOutlineState = "loading" | "generating" | "empty" | "ready";

export type TSlidesAction = {
  refresh: boolean;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
};

type TPresentationBuilderProps = {
  view: TViewMode;
  onViewChange: (view: TViewMode) => void;
  hasDeck: boolean;
  deckUpdating: boolean;
  outlineState: TOutlineState;
  outlineRefreshing: boolean;
  outlineMeta: string | null;
  canEdit: boolean;
  showInstructions: boolean;
  onOpenInstructions: () => void;
  onCreateOutline: () => void;
  refreshOutline: ReactNode;
  slidesAction: TSlidesAction | null;
  slideList: ReactNode;
  slideCards: ReactNode;
  slidesRef: Ref<HTMLDivElement>;
  onSlidesScroll: () => void;
  preview: ReactNode;
};

const STEPS = [
  { title: "presentationBuilder.steps.draftTitle", body: "presentationBuilder.steps.draftBody" },
  { title: "presentationBuilder.steps.editTitle", body: "presentationBuilder.steps.editBody" },
  { title: "presentationBuilder.steps.designTitle", body: "presentationBuilder.steps.designBody" }
] as const;

const VIEW_TOGGLE_ITEM =
  "h-7 gap-1.5 rounded-full! px-3 text-muted-foreground aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-xs";

// The outline and the deck fill the window under the pinned card header.
const BODY_HEIGHT = "h-[calc(100svh-var(--app-header-height)-11rem)] min-h-[30rem]";

export function PresentationBuilder({
  view,
  onViewChange,
  hasDeck,
  deckUpdating,
  outlineState,
  outlineRefreshing,
  outlineMeta,
  canEdit,
  showInstructions,
  onOpenInstructions,
  onCreateOutline,
  refreshOutline,
  slidesAction,
  slideList,
  slideCards,
  slidesRef,
  onSlidesScroll,
  preview
}: TPresentationBuilderProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const outlineReady = outlineState === "ready";
  const showingOutline = view === "outline" || !hasDeck;
  const showsOutlineTools = showingOutline && outlineReady;
  const showsRefreshOutline = showingOutline && (outlineReady || outlineRefreshing) && !!refreshOutline;
  const showsSlidesAction = showsOutlineTools && !!slidesAction;
  const hasTrailingActions =
    (showsOutlineTools && showInstructions) || showsRefreshOutline || deckUpdating || showsSlidesAction;

  const slidesActionLabel = !slidesAction
    ? ""
    : slidesAction.busy
    ? t(slidesAction.refresh ? "presentationBuilder.refreshingSlides" : "presentationBuilder.creatingSlides")
    : t(slidesAction.refresh ? "presentationBuilder.refreshSlides" : "presentationBuilder.createSlides");

  const actions = (
    <>
      <ToggleGroup
        value={[showingOutline ? "outline" : "preview"]}
        onValueChange={(next) => next[0] && onViewChange(next[0] as TViewMode)}
        spacing={0}
        aria-label={t("presentationBuilder.views")}
        className="rounded-full bg-muted p-0.5"
      >
        <ToggleGroupItem
          value="outline"
          size="sm"
          data-testid="presentation-back-to-outline"
          className={VIEW_TOGGLE_ITEM}
        >
          <ListNumbersIcon className="size-4" />
          {t("presentationBuilder.outline")}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="preview"
          size="sm"
          disabled={!hasDeck}
          data-testid="presentation-preview-slides"
          className={VIEW_TOGGLE_ITEM}
        >
          <MonitorPlayIcon className="size-4" />
          {t("presentationBuilder.slides")}
        </ToggleGroupItem>
      </ToggleGroup>
      {hasTrailingActions && <WidgetCardSeparator />}
      {showsOutlineTools && showInstructions && (
        <Button
          variant="ghost"
          size="icon-sm"
          shape="pill"
          aria-label={t("presentationBuilder.outlineInstructions")}
          title={t("presentationBuilder.outlineInstructions")}
          onClick={onOpenInstructions}
          data-testid="presentation-outline-instructions-button"
          className="text-muted-foreground"
        >
          <BookOpenTextIcon />
        </Button>
      )}
      {showsRefreshOutline && refreshOutline}
      {deckUpdating && (
        <span
          className="typo-caption flex items-center gap-1.5 text-muted-foreground"
          data-testid="presentation-slides-updating"
        >
          <Spinner aria-label={t("presentationBuilder.updatingSlides")} className="size-3.5" />
          {t("presentationBuilder.updatingSlides")}
        </span>
      )}
      {showsSlidesAction && slidesAction && (
        <Button
          size="sm"
          shape="pill"
          disabled={slidesAction.disabled}
          onClick={slidesAction.onClick}
          data-testid="presentation-create-slides"
        >
          {slidesAction.busy ? (
            <Spinner data-icon="inline-start" aria-label={slidesActionLabel} />
          ) : slidesAction.refresh ? (
            <ArrowClockwiseIcon data-icon="inline-start" />
          ) : (
            <SparkleIcon data-icon="inline-start" weight="fill" />
          )}
          {slidesActionLabel}
        </Button>
      )}
    </>
  );

  const outlineBody =
    outlineState === "loading" ? (
      <div className="flex flex-col gap-3 p-6" aria-busy="true">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    ) : outlineState === "generating" ? (
      <div className="flex min-h-[26rem] flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <Spinner aria-label={t("common:loading")} className="size-6 text-primary-text" />
        <h3 className="typo-h5 text-foreground">
          {t(outlineRefreshing ? "presentationBuilder.refreshingOutline" : "presentationBuilder.draftingOutline")}
        </h3>
        <p className="typo-body-sm max-w-[40ch] text-muted-foreground">
          {t("presentationBuilder.draftingDescription")}
        </p>
      </div>
    ) : outlineState === "empty" ? (
      <Empty className="py-12">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <PresentationChartIcon weight="duotone" />
          </EmptyMedia>
          <EmptyTitle>{t("presentationBuilder.emptyTitle")}</EmptyTitle>
          <EmptyDescription>{t("presentationBuilder.emptyDescription")}</EmptyDescription>
        </EmptyHeader>
        <ol className="grid w-full max-w-2xl gap-3 text-start sm:grid-cols-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="flex flex-col gap-1 rounded-xl bg-muted/50 p-4">
              <span className="typo-overline text-primary-text">
                {t("presentationBuilder.stepLabel", { number: index + 1 })}
              </span>
              <span className="typo-label-md text-foreground">{t(step.title)}</span>
              <span className="typo-caption text-muted-foreground">{t(step.body)}</span>
            </li>
          ))}
        </ol>
        {canEdit && (
          <EmptyContent className="flex-row flex-wrap justify-center gap-2">
            {showInstructions && (
              <Button
                variant="outline"
                shape="pill"
                onClick={onOpenInstructions}
                data-testid="outline-empty-instructions-button"
              >
                <BookOpenTextIcon data-icon="inline-start" />
                {t("presentationBuilder.outlineInstructions")}
              </Button>
            )}
            <Button shape="pill" onClick={onCreateOutline} data-testid="presentation-generate-outline">
              <SparkleIcon data-icon="inline-start" weight="fill" />
              {t("presentationBuilder.createOutline")}
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <div className={`flex ${BODY_HEIGHT}`}>
        <div className="flex min-h-0 shrink-0 flex-col border-e border-foreground/5">{slideList}</div>
        <div
          ref={slidesRef}
          onScroll={onSlidesScroll}
          className="min-h-0 min-w-0 flex-1 overflow-y-auto rounded-ee-2xl bg-muted/40 p-5"
        >
          <div className="mx-auto grid max-w-3xl gap-4 pb-4">{slideCards}</div>
        </div>
      </div>
    );

  return (
    <section
      data-testid="presentation-page"
      style={{ "--widget-sticky-gap": SECTION_STICKY_GAP } as CSSProperties}
      className="mx-auto flex w-full max-w-[960px] flex-col pb-8"
    >
      <h1 className="typo-h3 mb-6 min-h-10 text-foreground">{t("presentationBuilder.pageTitle")}</h1>
      <WidgetCard data-testid="presentation-builder">
        <WidgetCardHeader
          title={t("presentationBuilder.title")}
          titleAdornment={
            outlineMeta && showingOutline ? (
              <span className="typo-caption hidden shrink-0 text-muted-foreground sm:inline">{outlineMeta}</span>
            ) : undefined
          }
          stickyTop={sectionStickyTop(false)}
          actions={actions}
        />
        {showingOutline ? (
          outlineBody
        ) : (
          <div className={`flex justify-center overflow-hidden rounded-b-2xl bg-muted/40 p-4 ${BODY_HEIGHT}`}>
            {preview}
          </div>
        )}
      </WidgetCard>
    </section>
  );
}
